import React, { type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import {
  Accordion,
  AccordionButton,
  AccordionItem as ChakraAccordionItem,
  AccordionPanel,
  Text,
  Flex,
  Button,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import { Section } from "components/section";
import { ChevronDownIcon } from "ui/icons";
import { Link } from "ui/link";
import { type Integration, type JointIntegration } from "lib/sanity";
import { Balancer } from "react-wrap-balancer";

type FaqProps = {
  source: Integration;
  destination: Integration;
  jointIntegration?: JointIntegration;
};
export function Faq({ source, destination, jointIntegration }: FaqProps) {
  return (
    <Section>
      <Text as="h2" textStyle="h5" mb={{ base: 2, md: 6 }}>
        FAQs
      </Text>

      <Accordion allowToggle bg="white">
        {jointIntegration?.jointPitch && (
          <AccordionItem
            question={`Why sync data from ${source.name} to ${destination.name}?`}
            answer={
              <ReactMarkdown
                components={{
                  a: ({ node, href, ...props }) => <Link href={href} variant="article" size="lg" {...props} />,
                }}
              >
                {jointIntegration.jointPitch}
              </ReactMarkdown>
            }
          />
        )}

        <AccordionItem
          question="What are the benefits of syncing data with Hightouch?"
          answer={
            <>
              <Text>
                There are several options to sync data between sources. You can manually build and maintain a data
                pipeline, use a point-to-point solution such as Zapier, or you can manually upload CSVs.
              </Text>

              <Text mt={6}>With Hightouch, you get: </Text>

              <UnorderedList spacing={3} mt={3}>
                <ListItem>
                  <Text as="strong" fontWeight="semibold">
                    Automation:
                  </Text>{" "}
                  You do not need to build and maintain custom data pipelines and you do not have to have your team do
                  manual work
                </ListItem>

                <ListItem>
                  <Text as="strong" fontWeight="semibold">
                    Simplicity:
                  </Text>{" "}
                  You avoid a complex web of integrations caused by point-to-point solutions by syncing data from your
                  source
                </ListItem>

                <ListItem>
                  {" "}
                  <Text as="strong" fontWeight="semibold">
                    Speed:
                  </Text>{" "}
                  You can get set up in quickly - the average Hightouch customer starts syncing data in 23 minutes
                </ListItem>

                <ListItem>
                  <Text as="strong" fontWeight="semibold">
                    Control:
                  </Text>{" "}
                  companies of all sizes have access to enterprise-level controls including observability, dbt
                  integrations, and version control
                </ListItem>

                <ListItem>
                  <Text as="strong" fontWeight="semibold">
                    Security:
                  </Text>{" "}
                  Hightouch never stores your data and is HIPAA, GDPR, CCPA, and SOC-2 compliant
                </ListItem>
              </UnorderedList>

              <Text mt={6}>
                90% of all Hightouch syncs complete in 30 seconds or less, and the platform enables non-technical users
                to self-serve.
              </Text>
            </>
          }
        />

        <AccordionItem
          question="How frequently can I sync data?"
          answer={
            <Text>
              With Hightouch, you can sync data as frequently as it changes within your {source.name}. You can trigger
              data syncs manually or schedule them to run at an interval or custom recurrence as often as once per
              minute.
            </Text>
          }
        />

        <AccordionItem
          question="How does data mapping work?"
          answer={
            <Text>
              Hightouch offers a basic mapper or advanced mapper that allows you to visually match columns from your{" "}
              {source.name} to fields in {destination.name}.
            </Text>
          }
        />

        {source.vendorAbout && destination.vendorAbout && (
          <>
            <AccordionItem question={`What is ${source.name}?`} answer={<Text>{source.vendorAbout}</Text>} />

            <AccordionItem question={`What is ${destination.name}?`} answer={<Text>{destination.vendorAbout}</Text>} />
          </>
        )}

        {source.supportsInWarehouseDiffing && (
          <AccordionItem
            question="Does this integration support in-warehouse planning?"
            answer={
              <Flex direction="column" gap={6}>
                <Text>
                  Yes, if you integerate {source.name} and {destination.name} using Hightouch, in-warehouse planning is
                  supported.
                </Text>

                <Text>Great, but what is in-warehouse planning?</Text>

                <Text>
                  Between every sync, Hightouch notices any and all changes in your data model. This allows you to only
                  send updated results to your destination (in this case {destination.name}). With the baseline setup,
                  Hightouch picks out only the rows that need to be synced by querying every row in your data model
                  before diffing using Hightouch’s infrastructure.
                </Text>

                <Text>The issue here is this can be slow for large models.</Text>

                <Text>
                  Warehouse Planning allows Hightouch to do this diff directly in your warehouse. Read more on how this
                  works{" "}
                  <Link variant="article" href="/docs/syncs/lightning-sync-engine">
                    here
                  </Link>
                  .
                </Text>
              </Flex>
            }
          />
        )}
      </Accordion>
    </Section>
  );
}

function AccordionItem({ question, answer }: { question: string; answer: ReactNode }) {
  return (
    <ChakraAccordionItem borderTop={0} borderBottom="1px solid" borderBottomColor="base.border">
      {({ isExpanded }) => (
        <>
          <AccordionButton aria-label={question} py={4} px={0} role="group" _hover={{ bg: "white" }}>
            <Flex justify="space-between" w="full" align="center" textAlign="left" gap={4}>
              <Text as="h3" textStyle="e4">
                <Balancer>{question}</Balancer>
              </Text>

              <Button as="div" variant="white" transition="all .15s ease" p={1}>
                <ChevronDownIcon
                  color="text.primary"
                  boxSize={7}
                  transform={isExpanded ? "rotate(180deg)" : "none"}
                  transition="transform 300ms ease"
                />
              </Button>
            </Flex>
          </AccordionButton>

          <AccordionPanel fontSize={{ base: "md", md: "lg" }} maxW="5xl" mr={{ base: 0, md: 10 }} pb={10} pt={0} px={0}>
            {answer}
          </AccordionPanel>
        </>
      )}
    </ChakraAccordionItem>
  );
}
